import { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, DollarSign, AlertTriangle, Plus, Play, Pause, Trash2, Edit, CheckCircle, XCircle, RefreshCw, Loader2, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { format } from "date-fns";
import { DeployPortfolioAgentDialog } from "@/components/agentic/DeployPortfolioAgentDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PortfolioAgent {
  id: string;
  name: string;
  description: string;
  agent_type: string;
  status: string;
  config: Record<string, unknown>;
  created_at: string;
}

interface PortfolioAlert {
  id: string;
  agent_id: string;
  alert_type: string;
  severity: string;
  title: string;
  description: string;
  recommendation: string;
  metrics: Record<string, unknown>;
  status: string;
  created_at: string;
  portfolio_agents: {
    name: string;
  };
}

export default function PortfolioAgents() {
  const { orgId, familyId } = useTenant();
  const [agents, setAgents] = useState<PortfolioAgent[]>([]);
  const [alerts, setAlerts] = useState<PortfolioAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<PortfolioAgent | null>(null);

  useEffect(() => {
    fetchData();
  }, [orgId, familyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch agents
      const { data: agentData, error: agentError } = await supabase
        .from("portfolio_agents")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (agentError) throw agentError;
      setAgents(agentData || []);

      // Fetch alerts
      const { data: alertData, error: alertError } = await supabase
        .from("portfolio_agent_alerts")
        .select(`
          *,
          portfolio_agents (name)
        `)
        .eq("org_id", orgId)
        .eq("status", "open")
        .order("created_at", { ascending: false });

      if (alertError) throw alertError;
      setAlerts(alertData || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load portfolio agents");
    } finally {
      setLoading(false);
    }
  };

  const runAgentsNow = async () => {
    try {
      setRunning(true);
      const { data, error } = await supabase.functions.invoke("run-portfolio-agents", {
        body: { dryRun: false },
      });

      if (error) throw error;

      const { processed, alertsCreated, errors } = data || {};
      if (alertsCreated > 0) {
        toast.success(`Analysis complete: ${alertsCreated} new alert(s) created`);
      } else {
        toast.success(`Analysis complete: ${processed} agent(s) checked, no new alerts`);
      }

      if (errors?.length > 0) {
        console.error("Agent errors:", errors);
      }

      fetchData();
    } catch (err) {
      console.error("Error running agents:", err);
      toast.error("Failed to run agents: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setRunning(false);
    }
  };

  const toggleAgentStatus = async (agent: PortfolioAgent) => {
    const newStatus = agent.status === "active" ? "paused" : "active";
    
    try {
      const { error } = await supabase
        .from("portfolio_agents")
        .update({ status: newStatus })
        .eq("id", agent.id);

      if (error) throw error;

      toast.success(`Agent ${newStatus === "active" ? "activated" : "paused"}`);
      fetchData();
    } catch (err) {
      console.error("Error updating agent:", err);
      toast.error("Failed to update agent");
    }
  };

  const handleDeleteAgent = async () => {
    if (!selectedAgent) return;

    try {
      const { error } = await supabase
        .from("portfolio_agents")
        .delete()
        .eq("id", selectedAgent.id);

      if (error) throw error;

      toast.success("Agent deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedAgent(null);
      fetchData();
    } catch (err) {
      console.error("Error deleting agent:", err);
      toast.error("Failed to delete agent");
    }
  };

  const acknowledgeAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from("portfolio_agent_alerts")
        .update({
          status: "acknowledged",
          acknowledged_by: (await supabase.auth.getUser()).data.user?.id,
          acknowledged_at: new Date().toISOString(),
        })
        .eq("id", alertId);

      if (error) throw error;

      toast.success("Alert acknowledged");
      fetchData();
    } catch (err) {
      console.error("Error acknowledging alert:", err);
      toast.error("Failed to acknowledge alert");
    }
  };

  const dismissAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from("portfolio_agent_alerts")
        .update({
          status: "dismissed",
        })
        .eq("id", alertId);

      if (error) throw error;

      toast.success("Alert dismissed");
      fetchData();
    } catch (err) {
      console.error("Error dismissing alert:", err);
      toast.error("Failed to dismiss alert");
    }
  };

  const getAgentTypeIcon = (type: string) => {
    switch (type) {
      case "performance":
        return <TrendingUp className="h-4 w-4" />;
      case "exposure":
        return <AlertTriangle className="h-4 w-4" />;
      case "liquidity":
        return <DollarSign className="h-4 w-4" />;
      case "stale_data":
        return <Search className="h-4 w-4" />;
      default:
        return <TrendingUp className="h-4 w-4" />;
    }
  };

  const getAgentTypeBadge = (type: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      performance: "default",
      exposure: "destructive",
      liquidity: "secondary",
      stale_data: "secondary",
    };
    return variants[type] || "default";
  };

  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      low: "secondary",
      medium: "default",
      high: "destructive",
      critical: "destructive",
    };
    return variants[severity] || "default";
  };

  if (loading) {
    return <div className="flex-1 p-8">Loading...</div>;
  }

  return (
    <div className="flex-1 space-y-6 px-6 py-6">
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Portfolio Analysis Agents</h1>
          <p className="text-muted-foreground">
            Automated monitoring and recommendations for portfolio management
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={runAgentsNow}
            disabled={running || agents.filter(a => a.status === "active").length === 0}
          >
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {running ? "Running..." : "Run Now"}
          </Button>
          <Button onClick={() => setDeployDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Deploy Agent
          </Button>
        </div>
      </div>

      <Tabs defaultValue="agents" className="space-y-4">
        <TabsList>
          <TabsTrigger value="agents">Active Agents ({agents.length})</TabsTrigger>
          <TabsTrigger value="alerts">Open Alerts ({alerts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Deployed Agents</CardTitle>
              <CardDescription>
                Manage your portfolio monitoring agents
              </CardDescription>
            </CardHeader>
            <CardContent>
              {agents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No agents deployed yet.</p>
                  <p className="text-sm mt-2">Deploy your first agent to start monitoring your portfolio.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agents.map((agent) => (
                      <TableRow key={agent.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{agent.name}</div>
                            {agent.description && (
                              <div className="text-sm text-muted-foreground">{agent.description}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getAgentTypeBadge(agent.agent_type)} className="gap-1">
                            {getAgentTypeIcon(agent.agent_type)}
                            {agent.agent_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={agent.status === "active" ? "default" : "secondary"}>
                            {agent.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(agent.created_at), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleAgentStatus(agent)}
                            >
                              {agent.status === "active" ? (
                                <Pause className="h-4 w-4" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedAgent(agent);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          {alerts.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8 text-muted-foreground">
                <p>No open alerts.</p>
                <p className="text-sm mt-2">Your portfolio is being monitored.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {alerts.map((alert) => (
                <Card key={alert.id} className="border-l-4" style={{
                  borderLeftColor: alert.severity === "critical" || alert.severity === "high" 
                    ? "hsl(var(--destructive))" 
                    : alert.severity === "medium" 
                    ? "hsl(var(--warning))" 
                    : "hsl(var(--muted))"
                }}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-lg">{alert.title}</CardTitle>
                          <Badge variant={getSeverityBadge(alert.severity)}>
                            {alert.severity}
                          </Badge>
                        </div>
                        <CardDescription>
                          From: {alert.portfolio_agents.name} • {format(new Date(alert.created_at), "MMM d, yyyy h:mm a")}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => acknowledgeAlert(alert.id)}
                        >
                          <CheckCircle className="h-4 w-4" />
                          Acknowledge
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => dismissAlert(alert.id)}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {alert.description && (
                      <div>
                        <h4 className="text-sm font-medium mb-1">Description</h4>
                        <p className="text-sm text-muted-foreground">{alert.description}</p>
                      </div>
                    )}
                    {alert.recommendation && (
                      <div>
                        <h4 className="text-sm font-medium mb-1">Recommendation</h4>
                        <p className="text-sm">{alert.recommendation}</p>
                      </div>
                    )}
                    {alert.metrics && Object.keys(alert.metrics).length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2">Metrics</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {Object.entries(alert.metrics).map(([key, value]) => (
                            <div key={key} className="space-y-1">
                              <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                              <p className="text-sm font-medium">{String(value)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <DeployPortfolioAgentDialog 
        open={deployDialogOpen} 
        onOpenChange={setDeployDialogOpen}
        onSuccess={fetchData}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Agent</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedAgent?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAgent}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
